import useAPI from "@/libs/hooks/api";
import { useFirebase } from "@/libs/hooks/firebase";
import {
  EmailAuthProvider,
  linkWithCredential,
  linkWithPhoneNumber,
  linkWithPopup,
  onAuthStateChanged,
  PhoneAuthProvider,
  reauthenticateWithCredential,
  signOut,
  updatePassword,
  updateEmail as updateUserEmail,
  updatePhoneNumber as updateUserPhoneNumber
} from "firebase/auth";
import { doc, onSnapshot, query } from "firebase/firestore";
import { getToken, isSupported } from "firebase/messaging";
import { createContext, useCallback, useEffect, useState } from "react";

import { IS_LOCAL } from "@/constants";
import useCrisp from "@/libs/hooks/crisp";
import { useUpdateLastActive } from "@/libs/hooks/useUpdateLastActive";
import PostStatusCache from "@/libs/post-status-cache";
import { callStore } from "@/libs/stores/call.store";
import { streamsStore } from "@/libs/stores/streams.store";

const AuthContext = createContext({ user: null, authLoaded: false });

export const withAuthContext = (Component) => (props) => {
  const { auth, firestore, messaging, createVerifier } = useFirebase();
  const api = useAPI();
  const crisp = useCrisp({ load: false });

  const [user, setUser] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);

  useUpdateLastActive(firestore, auth);

  const extractClaimsFromToken = useCallback(async (firebaseUser) => {
    if (!firebaseUser?.getIdTokenResult) return {};
    try {
      const tokenResult = await firebaseUser.getIdTokenResult();
      const claims = tokenResult?.claims || {};
      const overrides = {};
      if (typeof claims.isCreator !== "undefined") {
        overrides.isCreator = claims.isCreator;
      }
      if (typeof claims.isAdmin !== "undefined") {
        overrides.isAdmin = claims.isAdmin;
      }
      if (typeof claims.creatorId !== "undefined") {
        overrides.creatorId = claims.creatorId;
      }
      return overrides;
    } catch (error) {
      console.error("Failed to read ID token claims", error);
      return {};
    }
  }, []);

  const reauthAndResetPassword = useCallback(
    async (password, newPassword) => {
      const currentUser = auth.currentUser;
      const providers = currentUser?.providerData || [];
      const passwordProvider = providers.find(
        (provider) => provider.providerId === "password"
      );
      if (passwordProvider) {
        await reauthenticateWithCredential(
          currentUser,
          EmailAuthProvider.credential(currentUser.email, password)
        );
      }
      await updatePassword(currentUser, newPassword);
    },
    [auth]
  );

  const linkProvider = useCallback(
    async (provider) => linkWithPopup(auth.currentUser, provider),
    [auth]
  );

  const linkPhoneNumber = useCallback(
    async (phone) =>
      linkWithPhoneNumber(
        auth.currentUser,
        phone,
        createVerifier("recaptcha", () => null)
      ),
    [auth, createVerifier]
  );

  const linkEmail = useCallback(
    async (email, password) =>
      linkWithCredential(
        auth.currentUser,
        EmailAuthProvider.credential(email, password)
      ),
    [auth]
  );

  const verifyPhoneNumber = useCallback(
    async (phone) =>
      new PhoneAuthProvider(auth).verifyPhoneNumber(
        phone,
        createVerifier("recaptcha", () => null)
      ),
    [auth, createVerifier]
  );

  const updatePhoneNumber = useCallback(
    async (verificationId, otp) => {
      const phoneCredential = PhoneAuthProvider.credential(verificationId, otp);
      return updateUserPhoneNumber(auth.currentUser, phoneCredential);
    },
    [auth.currentUser]
  );

  const updateEmail = useCallback(
    async (email) => updateUserEmail(auth.currentUser, email),
    [auth.currentUser]
  );

  const logout = useCallback(async () => {
    try {
      // try delete token from backend
      const token = await getToken(messaging);
      await api.deletePushToken(token);
    } catch (e) {
      console.error(e);
    }
    // Clear cached post statuses
    PostStatusCache.clearAll();
    setUser(null);
    signOut(auth);
    window.location.reload();
  }, [auth, messaging, api]);

  const requestToken = useCallback(async () => {
    const fcmSupported = await isSupported();

    if (fcmSupported && !IS_LOCAL) {
      // bind token device to user
      const permission = Notification.permission;
      if (permission === "granted") {
        const token = await getToken(messaging);
        api.addPushToken(token);
      }
    }
  }, [api, messaging]);

  useEffect(() => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        const [extraData, claimOverrides] = await Promise.all([
          api.getProfile(),
          extractClaimsFromToken(user)
        ]);
        const fullUser = { ...user, ...extraData, ...claimOverrides };
        
        setUser(fullUser);
        streamsStore.setIsAdminUser(fullUser?.isAdmin??false);
        callStore.setIsAdminUser(fullUser?.isAdmin??false);
        crisp.login(user);
      } else {
        streamsStore.setIsAdminUser(false);
        callStore.setIsAdminUser(false);
      }
      // set auth loaded true first so that it won't block rendering
      setAuthLoaded(true);
      if (user) requestToken();
    });
  }, [api, auth, crisp, requestToken, extractClaimsFromToken]);

  // listen user balance change
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(doc(firestore, "users", user.uid));
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const data = snapshot.data();
      const points = data?.points || { balance: 0 };
      const { pushTargets } = data;
      setUser((user) => ({ ...user, points, pushTargets, isCreator: data?.roles?.creator, isAdmin: data?.roles?.admin, creatorId: data?.creatorId }));
      streamsStore.setIsAdminUser(data?.roles?.admin??false);
      callStore.setIsAdminUser(data?.roles?.admin??false);
    });
    return () => unsubscribe();
  }, [api, firestore, user?.uid]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        authLoaded,
        requestToken,
        logout,
        linkProvider,
        linkPhoneNumber,
        linkEmail,
        updatePhoneNumber,
        updateEmail,
        verifyPhoneNumber,
        reauthAndResetPassword
      }}
    >
      <Component {...props} />
    </AuthContext.Provider>
  );
};

export default AuthContext;
